import Query from '@root/components/query';
import Customers from '@root/customers';
import { Customer, Languages } from '@root/models';

/*
 * The main class for the sign up page. 
 */
export class SignUp {
    private element: Element;

    constructor(element: Element) {
        this.element = element;
    }

    async init() {
        // Support case insensitive search for customized site values
        const customers = await Customers.all(),
            site = Query.get('site'),
            site_match = (Query.get('site') || '').toLowerCase();
        let customer = customers.find((cust: Customer) => cust.code.toLowerCase() === site_match) as Customer;
        
        // Use a default site if a specific one hasn't been found
        if (!customer)
            customer = customers.find((cust: Customer) => 'default' in cust) as Customer;
        
        // Generic Alertable signup page, no customizations and no SMS/email/phone
        if (!customer) {
            const emailButton = this.element.querySelector('#email-button'),
                phoneButton = this.element.querySelector('#phone-button'),
                alertMessageContent = this.element.querySelector('#alertmessage-description');
            if (emailButton)
                emailButton.classList.add('hidden');
            if (phoneButton)
                phoneButton.classList.add('hidden');
            if (alertMessageContent)
                alertMessageContent.classList.add('hidden');
            await this.hideLoader(100);
            return;
        }
        
        const iframe = this.element.querySelector('#alertMessage') as HTMLIFrameElement,
            language = document.documentElement.lang;

        // Applying customer configuration that affects the layout first to reduce
        // chances of layout visually sifting around during initialization

        // Default customer name in English, optional if different in French.
        // Ensure the customer name exists (they may not in custom sign-up pages, e.g. SaskAlert)
        const customer_name = this.element.querySelector('#customer-name');
        if (customer_name) {
            customer_name.innerHTML = customer.name;
            if (language === Languages.French && customer['name_fr'])
                customer_name.innerHTML = customer['name_fr'];
        }

        // Optionally show a customer logo
        const logo: HTMLImageElement = this.element.querySelector('#customer-logo');
        if (logo && customer['logo_url']) {
            logo.src = customer['logo_url'];
            // The customer logo img element is hidden in markup
            // then revealed here if logo_url is configured
            logo.classList.remove('hidden');
        }
        
        // Optionally apply customer-specific banner images
        const desktop_banner_en = customer['desktop_banner_en'],
            mobile_banner_en = customer['mobile_banner_en'],
            desktop_banner_fr = customer['desktop_banner_fr'],
            mobile_banner_fr = customer['mobile_banner_fr'];
        if (desktop_banner_en)
            this.updateBannerImage('#desktop-banner-en', desktop_banner_en);
        if (mobile_banner_en)
            this.updateBannerImage('#mobile-banner-en', mobile_banner_en);
        if (desktop_banner_fr)
            this.updateBannerImage('#desktop-banner-fr', desktop_banner_fr);
        if (mobile_banner_fr)
            this.updateBannerImage('#mobile-banner-fr', mobile_banner_fr);
        
        // Replace text with an optional customer-specific value
        const intro = customer[`intro_${language}`],
            content = customer[`content_${language}`],
            sub_title = customer[`subscribe_title_${language}`];
        if (content)
            this.element.querySelector('#content').innerHTML = content;
        else if (intro)
            this.element.querySelector('#intro').innerHTML = intro;
        if (sub_title)
            this.element.querySelector('#subscribe-title').innerHTML = sub_title;
        
        // If the customer sign-up page is configured to hide all channel buttons,
        // then hide the entire block of badges to minimize elements shifting around
        if (customer['hide_all_channel_buttons']) {
            const badge_block_element = this.element.querySelector('.badgeBlock');
            if (badge_block_element)
                badge_block_element.classList.add('hidden');
        } else {
            // Optionally hide specific buttons
            ['ios_button', 'android_button', 'alexa_button', 'facebook_button',
                'facebook_messenger_button', 'twitter_button', 'slack_button',
                'alertable_web_button', 'teams_button', 'email_button',
                'phone_button'].forEach((selector: string) => {
                if (customer[`hide_${selector}`]) {
                    let button_selector_elem = this.element.querySelector(
                        '#' + selector.replace(/_/g, '-'));
                    if (button_selector_elem)
                        button_selector_elem.classList.add('hidden');
                }
            });
        }
        
        // A customer sign-up page can also be configured to hide all or some channel descriptions
        if (customer['hide_all_channel_descriptions']) {
            const channel_descriptions_element = this.element.querySelector('#channel-descriptions');
            if (channel_descriptions_element)
                channel_descriptions_element.classList.add('hidden');
        } else {
            ['mobile_description', 'speaker_description', 'social_description',
                'messenger_description', 'alertable_web_description', 'work_apps_description',
                'alertmessage_description'].forEach((selector: string) => {
                if (customer[`hide_${selector}`]) {
                    let content_selector_elem = this.element.querySelector(
                        '#' + selector.replace(/_/g, '-'));
                    if (content_selector_elem)
                        content_selector_elem.classList.add('hidden');
                }
            });
        }
        
        let iframe_src_url = '';
        if (language === Languages.English && customer.iframeUrlEnglish)
            iframe_src_url = customer.iframeUrlEnglish;
        else if (language === Languages.French && customer.iframeUrlFrench)
            iframe_src_url = customer.iframeUrlFrench;
        // Pass through these optional parameters to the landing iframe
        if (iframe_src_url) {
            const iframe_urlObj = new URL(iframe_src_url);
            const iframe_passthrough_params = ['signin', 'silent'];
            iframe_passthrough_params.forEach(param => {
                const value = Query.get(param);
                if (value)
                    iframe_urlObj.searchParams.set(param, value);
            });
            iframe_src_url = iframe_urlObj.toString();
        }
        iframe.setAttribute('src', iframe_src_url);
        
        // Optionally expand the alertMessage sign up/sign in iframe by default
        if (customer['expand_alertmessage'])
            iframe.classList.remove('hidden');
        
        // Optionally hide the English/French toggle if bilingual not desired
        const language_toggle = this.element.querySelector('#language-toggle');
        if (language_toggle && customer['hide_language_toggle'])
            language_toggle.classList.add('hidden');
        
        // Add an optional thumbnail map
        const custom_map = customer[`map`];
        if (custom_map)
            this.element.querySelector('#map').setAttribute('src', custom_map);
        
        // Apply remaining customer configuration that is less likely to visually
        // shift the layout of page elements

        // Iterate through all of the iframe toggle anchors and ensure that their hrefs are set with
        // the correct HTML page and customer information.
        this.element.querySelectorAll('.alert-message-toggle').forEach((anchor: HTMLAnchorElement) => {
            // The button parameter is added to resolve accessibility warning about duplicate links
            anchor.href = `${language === Languages.French ? 'index-fr.html' : 'index.html'}${site ? '?site=' + site : ''}${'&button=' + anchor.id}#alertMessage`;
        });
        
        // Enables resizing the iframe when the vertical height changes.
        window.onmessage = e => {
            if (e.data.message === 'iframeScrolled' && scrollToBottomButton) {
                const scrollPos = iframe.contentWindow.scrollY;
                const scrollHeight = iframe.contentWindow.document.body.scrollHeight;
                const iframeHeight = iframe.clientHeight;
                if (scrollHeight - scrollPos - 2 <= iframeHeight) {
                    scrollToBottomButton.classList.add('hidden');
                } else {
                    scrollToBottomButton.classList.remove('hidden');
                }
            }
            //iframe.style.height = e.data.height + 'px'
        };

        // Replace default links with optional customer specific value
        const custom_email_link = customer[`email_link`];
        if (custom_email_link) {
            ['#email-button', '#email-link'].forEach((selector: string) => {
                const selector_elem = this.element.querySelector(selector);
                if (selector_elem) {
                    selector_elem.setAttribute('href', custom_email_link);
                    selector_elem.setAttribute('target', '_blank');
                    selector_elem.classList.remove('alert-message-toggle');
                }
            });
        }
        const custom_facebook_link = customer[`facebook_link`];
        if (custom_facebook_link) {
            ['#facebook-button', '#facebook-link'].forEach((selector: string) => {
                const selector_elem = this.element.querySelector(selector);
                if (selector_elem)
                    selector_elem.setAttribute('href', custom_facebook_link);
            });
        }
        const custom_twitter_link = customer[`twitter_link`];
        if (custom_twitter_link) {
            ['#twitter-button', '#twitter-link'].forEach((selector: string) => {
                const selector_elem = this.element.querySelector(selector);
                if (selector_elem)
                    selector_elem.setAttribute('href', custom_twitter_link);
            });
        }
        
        // Ensure that French/English links retain the site code
        ['#en-signup-link', '#fr-signup-link'].forEach((selector: string) => {
            const selector_elem = this.element.querySelector(selector);
            if (selector_elem) {
                let original_link = selector_elem.getAttribute('href');
                selector_elem.setAttribute('href', original_link + '?site=' + site);
            }
        });

        let scrollToBottomButton = this.element.querySelector('#scroll-to-bottom-btn');
        if (scrollToBottomButton) {
            scrollToBottomButton.addEventListener('click', () => {
                iframe.contentWindow.scrollTo(0, iframe.contentDocument.body.scrollHeight);
            });
        }

        await this.hideLoader(900);
    }


    // Function to check if content inside iframe is scrollable
    private isIframeContentScrollable(iframe) {
        var iframeDoc = iframe.contentWindow.document;
        var iframeBody = iframeDoc.body;
        return iframeBody.scrollHeight > iframeBody.clientHeight;
    }

    private updateBannerImage(bannerSelector: string, bannerSrc: string) {
        let banner = this.element.querySelector(bannerSelector);
        if (banner) {
            banner.setAttribute('src', bannerSrc);
        }
    }

    private async hideLoader(delay: number) {
        setTimeout(() => {
            this.element.querySelector("#signup-loader").classList.remove('show');
        }, delay);
    }
}